/**
 * API Log — vista Admin read-only del registro de llamadas a sistemas externos.
 *
 * Cada despacho/recepción/cancelación genera entradas en este log (ver v13 del
 * mockup vanilla). Sirve para auditoría regulatoria y debugging.
 *
 * En producción este log vive server-side con retención por fecha (deuda
 * documentada en Handoff GAMP 5).
 */

import { useMemo, useState } from 'react';
import { Search, Download, Activity, RefreshCw } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { toast } from '@/lib/notify';
import { getApiLog } from '@/api/endpoints/apilog';
import { useSessionStore } from '@/stores/session';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { fmtDateTimeLong } from '@/lib/format';
import { puedeActuar } from '@/domain/rbac';
import type { ApiLogEntry } from '@/domain/types';

export function ApiLogPage() {
  const usuario = useSessionStore((s) => s.usuario);
  const {
    data: entries = [],
    isFetching,
    refetch,
  } = useQuery({
    queryKey: ['api-log'],
    queryFn: () => getApiLog({ limit: 500 }),
    staleTime: 15_000,
  });

  const [sistemaFiltro, setSistemaFiltro] = useState<string>('all');
  const [metodoFiltro, setMetodoFiltro] = useState<string>('all');
  const [estadoFiltro, setEstadoFiltro] = useState<string>('all');
  const [busqueda, setBusqueda] = useState('');
  const [detalle, setDetalle] = useState<ApiLogEntry | null>(null);

  const filtradas = useMemo(() => {
    const q = busqueda.trim().toLowerCase();
    return entries.filter((e) => {
      if (sistemaFiltro !== 'all' && e.sistema !== sistemaFiltro) return false;
      if (metodoFiltro !== 'all' && e.metodo !== metodoFiltro) return false;
      if (estadoFiltro === 'ok' && !e.ok) return false;
      if (estadoFiltro === 'error' && e.ok) return false;
      if (!q) return true;
      return (
        e.endpoint.toLowerCase().includes(q) ||
        (e.operacionId ?? '').toLowerCase().includes(q) ||
        (e.errorMsg ?? '').toLowerCase().includes(q)
      );
    });
  }, [entries, sistemaFiltro, metodoFiltro, estadoFiltro, busqueda]);

  const kpis = useMemo(() => {
    const erp = entries.filter((e) => e.sistema === 'erp').length;
    const snt = entries.filter((e) => e.sistema === 'snt').length;
    const errs = entries.filter((e) => !e.ok).length;
    return { total: entries.length, erp, snt, errs };
  }, [entries]);

  // RBAC defensivo (la ruta también está protegida). Va DESPUÉS de los hooks.
  if (!puedeActuar(usuario, 'ver_audit_trail')) {
    return (
      <Alert variant="destructive">
        <AlertDescription>Acceso denegado. Requiere rol Administrador.</AlertDescription>
      </Alert>
    );
  }

  const exportarCsv = () => {
    const headers = ['Timestamp', 'Sistema', 'Método', 'Endpoint', 'Status', 'Op ID', 'Error'];
    const rows = filtradas.map((e) => [
      e.ts,
      e.sistema,
      e.metodo,
      e.endpoint,
      String(e.status),
      e.operacionId ?? '',
      e.errorMsg ?? '',
    ]);
    const csv = [headers, ...rows]
      .map((row) => row.map((v) => `"${v.replace(/"/g, '""')}"`).join(','))
      .join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `api-log-${new Date().toISOString().slice(0, 10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success(`Exportadas ${filtradas.length} entradas a CSV`);
  };

  return (
    <div className="space-y-6">
      {/* KPIs */}
      <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
        <KpiCard label="Total" value={kpis.total} />
        <KpiCard label="ERP" value={kpis.erp} tone="blue" />
        <KpiCard label="SNT (ANMAT)" value={kpis.snt} tone="purple" />
        <KpiCard label="Errores" value={kpis.errs} tone="red" />
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-everex-azul">
            <Activity className="h-5 w-5" />
            Logs de API (ERP + SNT)
          </CardTitle>
          <p className="text-xs text-gray-600">
            Registro server-side de las comunicaciones con ERP (ACSIS) y SNT (riga).
            Se muestran las últimas 500.
          </p>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="grid grid-cols-1 gap-3 md:grid-cols-5">
            <div className="md:col-span-2">
              <Label className="mb-1 block text-xs">Buscar</Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-600" />
                <Input
                  className="pl-9 font-mono"
                  placeholder="Endpoint, Op ID, error..."
                  value={busqueda}
                  onChange={(e) => setBusqueda(e.target.value)}
                />
              </div>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Sistema</Label>
              <Select value={sistemaFiltro} onValueChange={setSistemaFiltro}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="erp">ERP Plataforma</SelectItem>
                  <SelectItem value="snt">SNT (ANMAT)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Método</Label>
              <Select value={metodoFiltro} onValueChange={setMetodoFiltro}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="GET">GET</SelectItem>
                  <SelectItem value="POST">POST</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="mb-1 block text-xs">Estado</Label>
              <Select value={estadoFiltro} onValueChange={setEstadoFiltro}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos</SelectItem>
                  <SelectItem value="ok">OK (2xx)</SelectItem>
                  <SelectItem value="error">Error (4xx/5xx)</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="flex items-center justify-between text-[11px] text-gray-600">
            <span>
              Mostrando {filtradas.length} de {entries.length} entradas
            </span>
            <div className="flex gap-2">
              <Button size="sm" variant="outline" onClick={() => refetch()} disabled={isFetching}>
                <RefreshCw className={cn('mr-1 h-3 w-3', isFetching && 'animate-spin')} />
                Actualizar
              </Button>
              <Button size="sm" variant="outline" onClick={exportarCsv}>
                <Download className="mr-1 h-3 w-3" />
                Exportar CSV
              </Button>
            </div>
          </div>

          <div className="overflow-x-auto rounded border border-gray-100">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>Sistema</TableHead>
                  <TableHead>Método</TableHead>
                  <TableHead>Endpoint</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Op ID</TableHead>
                  <TableHead />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtradas.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} className="py-8 text-center text-sm text-gray-600">
                      {isFetching
                        ? 'Cargando registros…'
                        : 'Sin registros todavía. Cada llamada al ERP o al SNT (sincronizar, trazar, cumplir tarea) genera una entrada.'}
                    </TableCell>
                  </TableRow>
                ) : (
                  filtradas.slice(0, 200).map((e) => (
                    <TableRow key={e.id}>
                      <TableCell className="font-mono text-[11px]">
                        {fmtDateTimeLong(e.ts)}
                      </TableCell>
                      <TableCell>
                        <span
                          className={cn(
                            'badge',
                            e.sistema === 'erp' ? 'badge-blue' : 'badge-purple',
                          )}
                        >
                          {e.sistema === 'erp' ? 'ERP' : 'SNT'}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-xs">{e.metodo}</TableCell>
                      <TableCell className="max-w-md truncate font-mono text-[11px]">
                        {e.endpoint}
                      </TableCell>
                      <TableCell>
                        <span className={cn('badge', e.ok ? 'badge-green' : 'badge-red')}>
                          {e.status}
                        </span>
                      </TableCell>
                      <TableCell className="font-mono text-[11px]">
                        {e.operacionId ?? '—'}
                      </TableCell>
                      <TableCell>
                        <Button size="sm" variant="ghost" onClick={() => setDetalle(e)}>
                          Ver
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modal de detalle JSON */}
      <Dialog open={detalle !== null} onOpenChange={(o) => { if (!o) setDetalle(null); }}>
        <DialogContent className="max-h-[80vh] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Detalle del registro · {detalle?.id}</DialogTitle>
          </DialogHeader>
          {detalle && (
            <div className="space-y-3">
              <div className="grid grid-cols-2 gap-2 text-xs">
                <Info label="Sistema">{detalle.sistema.toUpperCase()}</Info>
                <Info label="Método">{detalle.metodo}</Info>
                <Info label="Endpoint">{detalle.endpoint}</Info>
                <Info label="Status">{detalle.status}</Info>
                <Info label="Timestamp">{fmtDateTimeLong(detalle.ts)}</Info>
                <Info label="Op ID">{detalle.operacionId ?? '—'}</Info>
              </div>
              {detalle.requestBody !== undefined && (
                <div>
                  <Label className="text-xs">Request body</Label>
                  <pre className="mt-1 overflow-x-auto rounded bg-gray-50 p-3 font-mono text-[11px]">
                    {JSON.stringify(detalle.requestBody, null, 2)}
                  </pre>
                </div>
              )}
              {detalle.responseBody !== undefined && (
                <div>
                  <Label className="text-xs">Response body</Label>
                  <pre className="mt-1 overflow-x-auto rounded bg-gray-50 p-3 font-mono text-[11px]">
                    {JSON.stringify(detalle.responseBody, null, 2)}
                  </pre>
                </div>
              )}
              {detalle.errorMsg && (
                <Alert variant="destructive">
                  <AlertDescription className="text-xs">{detalle.errorMsg}</AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Info({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[11px] uppercase text-gray-600">{label}</div>
      <div className="font-mono text-xs">{children}</div>
    </div>
  );
}

function KpiCard({
  label,
  value,
  tone = 'default',
}: {
  label: string;
  value: number;
  tone?: 'default' | 'blue' | 'purple' | 'red';
}) {
  const toneClass = {
    default: 'text-everex-azul',
    blue: 'text-blue-700',
    purple: 'text-purple-700',
    red: 'text-red-700',
  }[tone];
  return (
    <Card>
      <CardContent className="pt-4">
        <div className={cn('text-3xl font-bold', toneClass)}>{value}</div>
        <div className="text-[11px] uppercase text-gray-600">{label}</div>
      </CardContent>
    </Card>
  );
}
